'use client';

import Breadcrumbs from '@/components/breadcrumbs/Breadcrumbs';
import HeadlineField from '@/components/customFields/HeadlineField';
import { HeadlineTag, type StoryblokLinkResource } from '@/components/customFields/types';
import InlineTextLink from '@/components/inlineTextLink/InlineTextLink';
import NestedBlocks from '@/components/nestedBlocks/NestedBlocks';
import { RichText } from '@/components/richText/RichText';
import { GuideContext } from '@/components/styleguideComponents/guideLayout/guideLayout';
import type { ComponentLibraryChapterStoryblok, PageComponentLibraryStoryblok } from '@/types/component-types-sb';
import { storyblokEditable } from '@storyblok/react/rsc';
import classNames from 'classnames';
import { usePathname } from 'next/navigation';
import React, { useContext } from 'react';

import classes from './guidePage.module.scss';

type GuidePageProps = { blok: PageComponentLibraryStoryblok };

const GuidePage: React.FC<GuidePageProps> = ({ blok }) => {
  const storyList = useContext(GuideContext);
  const pathname = usePathname();
  const segments = pathname.split('/').filter((el) => el && el !== 'guide');
  const links: StoryblokLinkResource[] = segments
    .map((_, i) => 'guide/' + segments.slice(0, i + 1).join('/'))
    .map((path) => storyList.find((story) => story.slug === path))
    .filter((el): el is StoryblokLinkResource => el !== undefined);

  return (
    <section
      aria-label="Seiteninhalt"
      {...storyblokEditable(blok)}
      data-testid="style-guide-main-page-container"
      className={classNames(classes.guidePageContainer, blok.background_white ? classes.whiteBackground : '')}
    >
      <div className={classes.pageHeader}>
        <div className={classNames('grid-wrapper', classes.innerBox)}>
          <h1 className={classes.pageHeader__title}>{blok.title}</h1>
          <RichText className={classes.pageHeader__description} text={blok.description}></RichText>
        </div>
      </div>
      <div className={classNames('grid-wrapper', classes.pageContent)}>
        <Breadcrumbs links={links} homeLink={'/guide'} excludeFolderLinks alwaysVisible />
        {blok.show_menu && (
          <div className={classes.linksBox}>
            <HeadlineField className={classes.linksBox__title} tag={HeadlineTag.H2} style={HeadlineTag.H4}>
              Inhalt
            </HeadlineField>
            <div className={classes.linksBox__links}>
              <ul>
                {blok.components?.map((nestedBlok: ComponentLibraryChapterStoryblok, index: number) => (
                  <li data-testid={'style-guide-main-page-link-item' + Number(index + 1)} key={nestedBlok._uid}>
                    <InlineTextLink href={'#' + nestedBlok._uid}>{nestedBlok.title}</InlineTextLink>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}
        <NestedBlocks blocks={blok.components} />
      </div>
    </section>
  );
};

export default GuidePage;
